import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Clock, Gauge, MapPin, Route, Search } from "lucide-react";
import { toast } from "sonner";
import { Empty, Field, NSelect, PageHeader } from "@/components/common";
import { useLeafletMap } from "@/hooks/useLeafletMap";
import { drawRoute } from "@/components/map/markers";
import { useFleet } from "@/context/FleetContext";
import { api, errMsg } from "@/lib/api";
import { dateInput, daysAgo, fmtDuration, fmtShort, toIso } from "@/lib/fleet";
import { cn } from "@/lib/utils";

export function HistoryView({ fixedVehicleId }) {
  const { vehicles } = useFleet();
  const [params] = useSearchParams();
  const [vid, setVid] = useState(fixedVehicleId || params.get("v") || "");
  const [from, setFrom] = useState(dateInput(daysAgo(3)));
  const [to, setTo] = useState(dateInput());
  const [trips, setTrips] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const map = useLeafletMap(mapRef);
  const layer = useRef(null);

  useEffect(() => {
    if (!vid && vehicles.length) setVid(vehicles[0].id);
  }, [vehicles, vid]);

  const load = async () => {
    if (!vid) return;
    setLoading(true);
    setActive(null);
    try {
      const { data } = await api.get(`/vehicles/${vid}/trips`, { params: { start: toIso(from, "00:00"), end: toIso(to, "23:59") } });
      setTrips(data.reverse());
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vid) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid]);

  const showTrip = async (t) => {
    setActive(t.id);
    const { data } = await api.get(`/vehicles/${vid}/positions`, { params: { start: t.start_time, end: t.end_time } });
    if (layer.current) layer.current.remove();
    layer.current = drawRoute(map, data.positions, { stops: t.stops });
  };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[420px_1fr]">
      <div className="space-y-4">
        <div className="gt-card grid grid-cols-2 gap-3 p-4">
          {!fixedVehicleId && (
            <Field label="Kendaraan" className="col-span-2">
              <NSelect value={vid} onChange={setVid} testId="history-vehicle-select">
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}
              </NSelect>
            </Field>
          )}
          <Field label="Dari"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="gt-input" data-testid="history-from-date" /></Field>
          <Field label="Sampai"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="gt-input" data-testid="history-to-date" /></Field>
          <button onClick={load} className="col-span-2 flex h-10 items-center justify-center gap-2 rounded-xl bg-primary text-sm font-bold text-primary-foreground hover:opacity-90" data-testid="history-search-btn">
            <Search className="h-4 w-4" /> {loading ? "Memuat..." : "Tampilkan Perjalanan"}
          </button>
        </div>
        <div className="gt-card gt-scroll max-h-[560px] overflow-y-auto p-2" data-testid="history-trip-list">
          <div className="flex items-center justify-between px-2 py-2">
            <span className="text-sm font-bold">Perjalanan</span>
            <span className="font-mono text-xs text-muted-foreground">{trips?.length ?? 0} trip · {(trips || []).reduce((a, t) => a + t.distance_km, 0).toFixed(1)} km</span>
          </div>
          {(trips || []).map((t, i) => (
            <button key={t.id} onClick={() => showTrip(t)} data-testid={`history-trip-${i}`}
              className={cn("mb-1 w-full rounded-xl border p-3 text-left transition-colors", active === t.id ? "border-primary/60 bg-primary/[0.07]" : "border-transparent hover:bg-accent")}>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold">{new Date(t.start_time).toLocaleDateString("id-ID", { weekday: "short", day: "2-digit", month: "short" })}</span>
                <span className="font-mono text-sm font-bold">{t.distance_km.toFixed(1)} km</span>
              </div>
              <div className="mt-1 font-mono text-xs text-muted-foreground">{fmtShort(t.start_time)} → {new Date(t.end_time).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDuration(t.duration_s)}</span>
                <span className="flex items-center gap-1"><Gauge className="h-3 w-3" /> {Math.round(t.max_speed)} km/j</span>
                <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {t.stop_count} berhenti</span>
              </div>
            </button>
          ))}
          {trips && !trips.length && <Empty icon={Route} title="Tidak ada perjalanan" text="Tidak ada trip pada rentang tanggal ini." />}
        </div>
      </div>
      <div className="gt-card relative h-[420px] overflow-hidden lg:sticky lg:top-24 lg:h-[680px]" data-testid="history-map">
        <div ref={mapRef} className="absolute inset-0" />
        {!active && <div className="pointer-events-none absolute left-1/2 top-4 z-[500] -translate-x-1/2 rounded-full border border-border bg-card/90 px-3 py-1.5 text-xs font-semibold backdrop-blur">Klik perjalanan untuk menampilkan rute</div>}
      </div>
    </div>
  );
}

export default function HistoryPage() {
  return (
    <div>
      <PageHeader eyebrow="Riwayat" title="Histori Perjalanan" subtitle="Tanggal, mulai, selesai, durasi, jarak, kecepatan maksimum dan jumlah berhenti setiap trip." />
      <HistoryView />
    </div>
  );
}
